import { Button } from 'components/atoms/Button';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import type { ActivationStepProps } from '../../types';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';

// Shared images for all languages
import image0 from './images/0.webp';
import image1 from './images/1.webp';

// Dynamic animation loaders by language
const ANIMATION_LOADERS: Record<string, () => Promise<{ default: any }>> = {
  en: () => import('./animations/en.json'),
  de: () => import('./animations/de.json'),
  fr: () => import('./animations/fr.json'),
  nl: () => import('./animations/nl.json'),
};

// Shared image map (same images for all languages)
const IMAGE_MAP: Record<string, string> = {
  '0.webp': image0,
  '1.webp': image1,
};

/**
 * Fixes image paths in Lottie animation data to use webpack-imported assets
 */
const patchAnimationImages = (animationData: any): any => {
  const clonedData = JSON.parse(JSON.stringify(animationData));

  if (clonedData.assets) {
    clonedData.assets = clonedData.assets.map((asset: any) => {
      if (asset.p && IMAGE_MAP[asset.p]) {
        return {
          ...asset,
          u: '', // Clear relative base path
          p: IMAGE_MAP[asset.p], // Use webpack absolute path
        };
      }
      return asset;
    });
  }

  return clonedData;
};

/**
 * Explains how taxes are automatically calculated and reserved
 * UI-only informational step with language-specific animations
 */
const TaxesDescriptionStep: FC<ActivationStepProps> = ({
  onContinue,
  onBack,
  // Wrapper props (automatically injected)
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  isUpdatingStep,
}) => {
  const { t, i18n } = useTranslation();
  const [animationData, setAnimationData] = useState<any>(null);

  // Load animation dynamically based on language
  useEffect(() => {
    const loader = ANIMATION_LOADERS[i18n.language] || ANIMATION_LOADERS.en;
    loader().then((module) => {
      const patched = patchAnimationImages(module.default);
      setAnimationData(patched);
    });
  }, [i18n.language]);

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton
      onBack={onBack}
    >
      <div className="flex justify-center mb-8" style={{ minHeight: 220 }}>
        {animationData && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData,
                rendererSettings: {
                  preserveAspectRatio: 'xMidYMid meet',
                },
              }}
              height={220}
              width={358}
            />
          </motion.div>
        )}
      </div>

      <Button
        onClick={onContinue}
        className="w-full"
        color="bank"
        disabled={isUpdatingStep}
      >
        {t('continue')}
      </Button>
    </OnboardingStepWrapper>
  );
};

export default TaxesDescriptionStep;
